import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon, ChevronRightIcon } from '@utils/icons';
import { PAGE_SIZE_OPTIONS, usePageSize } from './PageSizeContext';
import type { IBreakdownPager } from './usePagination';
import table from './breakdownTable.module.css';

interface IBreakdownTableFooterProps {
  pager: IBreakdownPager;
}

/** Footer pager for a paginated breakdown table: rows-per-page selector, the
 *  visible range, and previous/next controls. Takes the usePagination result
 *  whole and owns the render-nothing-when-one-page rule, so every panel's
 *  footer is the single line `<BreakdownTableFooter pager={pager} />`.
 *  Rendered as a <tfoot> row spanning every column, so it sits flush under
 *  the body regardless of how many columns a panel has. The page size is
 *  shared across all breakdown tables via PageSizeContext. */
export function BreakdownTableFooter({ pager }: IBreakdownTableFooterProps) {
  const { t } = useTranslation();
  const { pageSize, setPageSize } = usePageSize();
  if (!pager.hasPages) return null;
  const { total, page, pageCount, rangeStart, rangeEnd, goPrev, goNext } = pager;
  return (
    <tfoot>
      <tr>
        {/* colSpan large enough to cover any panel's column count */}
        <td colSpan={100} className={table.paginationCell}>
          <div className={table.pagination}>
            <label className={table.pageSizeLabel}>
              {t('productivity.pagination.rowsPerPage')}
              <select
                className={table.pageSizeSelect}
                value={pageSize}
                onChange={(event) => setPageSize(Number(event.target.value))}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
            <span className={table.paginationRange}>
              {t('productivity.pagination.range', { start: rangeStart, end: rangeEnd, total })}
            </span>
            <button
              type="button"
              className={table.pageBtn}
              onClick={goPrev}
              disabled={page === 0}
              aria-label={t('productivity.pagination.prev')}
            >
              <ChevronLeftIcon size={14} />
            </button>
            <button
              type="button"
              className={table.pageBtn}
              onClick={goNext}
              disabled={page >= pageCount - 1}
              aria-label={t('productivity.pagination.next')}
            >
              <ChevronRightIcon size={14} />
            </button>
          </div>
        </td>
      </tr>
    </tfoot>
  );
}
